/* oxlint-disable max-lines-per-function */
import { type GroupChannelListItemProps } from '@sendbird/uikit-react/GroupChannelList/components/GroupChannelListItem';
import clsx from 'clsx';
import moment from 'moment';
import { useCallback, useMemo, useEffect, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { MemberAvatars } from './MemberAvatars';
import { getRelativeTimeLocaleKey } from '../utils/getRelativeTimeLocaleKey';
import { isImageMessage, isVideoMessage, useMessagingConfig, useGetChannelName } from '../utils';
import { type SerializedChannel } from '../messaging.types';
import { setActiveChannelUrl, setShowChannelInfo, registerChannel, setShowProfileUserId } from '../actions';

const classes = {
    container: (isSelected: boolean) =>
        clsx(
            'h-[84px]',
            'w-full',
            'min-w-[320px]',
            'border-s-[5px]',
            'border-solid',
            'flex',
            'justify-start',
            'items-center',
            'transition-all',
            'ps-[11px]',
            'pe-[16px]',
            {
                'border-transparent bg-white hover:bg-slate-grey-extra-light': !isSelected,
                'border-black bg-slate-grey-extra-light': isSelected,
            },
        ),
    avatar: {
        container: clsx('shrink-0', 'h-[56px]', 'w-[56px]', 'me-2'),
    },
    channelInfo: {
        container: clsx('flex-grow'),
        nameAndDate: {
            container: clsx('flex', 'justify-between', 'items-center', 'w-full', 'h-[1em]'),
            name: clsx(
                'font-semibold',
                'text-black',
                'text-[16px]',
                'line-clamp-1',
                'grow-1',
                'overflow-ellipsis',
                'text-start',
            ),
            date: clsx('shrink-0', 'text-slate-grey', 'text-xxs'),
        },
        lastMessage: {
            container: clsx('flex'),
            message: clsx(
                'text-slate-grey',
                'text-[13px]',
                'text-start',
                'line-clamp-2',
                'overflow-ellipsis',
                'grow',
                'shrink',
                'leading-[1.2]',
                'h-[32px]',
            ),
            unreadCount: clsx(
                'flex',
                'justify-center',
                'items-center',
                'min-w-[20px]',
                'h-[20px]',
                'ms-2',
                'px-1',
                'rounded-full',
                'bg-coral',
                'text-white',
                'text-[12px]',
                'font-semibold',
                'grow-0',
                'shrink-0',
                'self-center',
            ),
        },
    },
};

export const ChannelListItem = memo(({ channel, isSelected = false }: GroupChannelListItemProps) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'messaging' });
    const { userId } = useMessagingConfig();
    const dispatch = useDispatch();

    const serializedChannel = useMemo(() => channel.serialize() as SerializedChannel, [channel]);

    const membersThatAreNotMe = useMemo(
        () => channel.members.filter(member => member.userId !== userId),
        [channel.members, userId],
    );

    const getChannelName = useGetChannelName();

    const channelName = useMemo(
        () => getChannelName(serializedChannel, userId!),
        [userId, serializedChannel, getChannelName],
    );

    const relativeTimeLocaleKey = useMemo(() => {
        if (!channel.lastMessage?.createdAt) return null;
        return getRelativeTimeLocaleKey(new Date(channel.lastMessage.createdAt));
    }, [channel.lastMessage]);

    const handleChannelClick = useCallback(() => {
        dispatch(setActiveChannelUrl(channel.url));
        dispatch(setShowChannelInfo(false));
        dispatch(setShowProfileUserId(null));
    }, [dispatch, channel.url]);

    const prevChannelUrlRef = useRef('');
    useEffect(() => {
        // Only register if the channel URL is new (avoid re-dispatching on every Sendbird update)
        if (serializedChannel.url !== prevChannelUrlRef.current) {
            prevChannelUrlRef.current = serializedChannel.url;
            dispatch(registerChannel(serializedChannel));
        }
    }, [dispatch, serializedChannel]);

    return (
        <button
            type="button"
            className={classes.container(isSelected)}
            title={channelName}
            onClick={handleChannelClick}
        >
            <MemberAvatars
                className={classes.avatar.container}
                members={membersThatAreNotMe}
                showOnlineStatus={membersThatAreNotMe.length === 1}
            />
            <span className={classes.channelInfo.container}>
                <span className={classes.channelInfo.nameAndDate.container}>
                    <span className={classes.channelInfo.nameAndDate.name}>
                        {membersThatAreNotMe.length === 0 ? `(${t('messaging.no_members')})` : channelName}
                    </span>
                    <span className={classes.channelInfo.nameAndDate.date}>
                        {relativeTimeLocaleKey
                            ? t(`messaging.${relativeTimeLocaleKey}`, {
                                  formattedDate: moment(new Date(channel.lastMessage!.createdAt)).format('M/D/YY'),
                              })
                            : null}
                    </span>
                </span>
                <span className={classes.channelInfo.lastMessage.container}>
                    <span className={classes.channelInfo.lastMessage.message}>
                        {!!channel.lastMessage &&
                            (isImageMessage(channel.lastMessage)
                                ? `(${t('messaging.image')}) ${channel.lastMessage.name}`
                                : isVideoMessage(channel.lastMessage)
                                  ? `(${t('messaging.video')}) ${channel.lastMessage.name}`
                                  : channel.lastMessage?.isFileMessage()
                                    ? `(${t('messaging.file')}) ${channel.lastMessage.name}`
                                    : channel.lastMessage?.message)}
                    </span>
                    {channel.unreadMessageCount > 0 && (
                        <span className={classes.channelInfo.lastMessage.unreadCount}>
                            {channel.unreadMessageCount}
                        </span>
                    )}
                </span>
            </span>
        </button>
    );
});
